import React, { useState, ChangeEvent } from "react";

import { AccountType, useAccount } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";

import { useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";

import AccountService from "@features/createaccount/service/AccountService";

import { ComposeURLWithGetQueryArguments } from "@shared/utils";
import { PagePaths } from "@shared/constants";
import { TimeForNewVerify } from "@components/ui";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_insertUsernameCode } from "@i18n/usualTexts/t_insertUsernameCode";
import {
  cleanFormDataErrors,
  cleanStringsObject,
} from "@shared/utils";
import { delay, intervalMiliseconds } from "@shared/utils";
import { RegisterCommitPost, SendConfirmationCodePost } from "@features/createaccount/service/AccountRequest";
import { useAuthError } from "@shared/hooks/useAuthError";

interface FormData {
  usernamecode: {
    value: string;
    errors: Set<string>;
  };
}

const InsertUsernameCodePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t("signup.insertUsernameCode.pageTitle"));
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { accountData, setAccountData } = useAccount();
  const { currentToken: antiBotToken, executeRecaptcha } = useRecaptchaContext();
  const { handleRegisterError } = useAuthError();
  const [formData, setFormData] = useState<FormData>({
    usernamecode: {
      value: "",
      errors: new Set<string>(),
    },
  });

  return (
    <AuthenticationForm
      title={t(t_insertUsernameCode.title)}
      subtitle={t(t_insertUsernameCode.subtitle)}
      back={back}
      onSubmit={() => handleSubmit()}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <div>
        <FormField
          autofocus={true}
          name="usernamecode"
          type="text"
          required={true}
          onChange={handleFormData}
          value={formData.usernamecode.value}
          errors={formData.usernamecode.errors}
          submitted={submitted}
        >
          {t(t_insertUsernameCode.codePlaceholder)}
        </FormField>
      </div>

      <TimeForNewVerify sendCodeEmail={sendCodeEmail} />
    </AuthenticationForm>
  );

  async function sendCodeEmail() {
    console.log("send code email");
    console.log("accountData:", JSON.stringify(accountData, null, 2));
    console.log("passwordPostResponse:", accountData.passwordPostResponse);
    const token = accountData.passwordPostResponse?.access_token;
    console.log("token for resend:", token);
    if (!token) {
      console.error("No token available for resend - passwordPostResponse is missing from context");
      return;
    }
    try {
      await SendConfirmationCodePost(token, antiBotToken || undefined);
    } catch (error) {
      console.error("Send confirmation code error:", error);
    }
  }

  function handleFormData(e: ChangeEvent<HTMLInputElement>) {
    const { value, name } = e.target;

    formData[name as keyof FormData].value = value;
    setFormData({
      ...formData,
    });
  }

  function back() {
    setAccountData({
      ...accountData,
      step: 4,
    });

    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.CREATEPASSWORD));
  }

  async function handleSubmit() {
    setSubmitted(true);
    setIsLoading(true);

    const step = 6;

    const newAccountData: AccountType = {
      ...accountData,
      step: step,
    };

    try {
      checkThisFormData();

      await post_and_navigate(newAccountData);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      console.log(error)
      handleRegisterError(error, setServerErrorPopUp, frontExceptionFunction);
    } finally {
      setIsLoading(false);

      setFormData({
        ...formData,
      });

      await delay(1000);
      setSubmitted(false);
    }

    setFormData({
      ...formData,
    });
  }

  async function post_and_navigate(newAccountData: AccountType) {
    const passwordPostResponse = accountData.passwordPostResponse;
    if (!passwordPostResponse) {
      throw new Error(t("error.runtime.noPasswordResponse"));
    }

    const intervalMilisecondsValue = intervalMiliseconds(passwordPostResponse);
    await delay(intervalMilisecondsValue);

    const freshToken = await executeRecaptcha();
    const commitPostResponse = await RegisterCommitPost(
      formData.usernamecode.value,
      passwordPostResponse?.access_token,
      freshToken?.token || antiBotToken || undefined
    );
    console.log(commitPostResponse);

    cleanStringsObject(accountData);

    setAccountData({
      ...accountData,
      step: newAccountData.step,
    });

    navigate(
      ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.CONGRATULATIONS)
    );
  }

  function checkThisFormData() {
    cleanFormDataErrors(formData);

    const accountService: AccountService = new AccountService(t);
    accountService.checkCode(formData.usernamecode.value, "usernamecode");
  }

  function frontExceptionFunction(errorDescription: Array<string>) {
    formData[errorDescription[0] as keyof FormData].errors.add(
      errorDescription[1]
    );
  }
};

export default InsertUsernameCodePage;
